import React, { useMemo } from 'react';

const PreviewPane = ({ template, values = {}, isPdfExport = false }) => {
    const renderedContent = useMemo(() => {
        if (!template?.blocks) return [];

        return template.blocks
            .filter((block) => {
                // Skip optional clauses that are disabled
                if (block.type === 'clause' && block.optional && !block.enabled) {
                    return false;
                }
                return true;
            })
            .map((block) => {
                // Use content or text field
                let text = block.content || block.text || '';

                // Replace variables in text - clean format without highlighting
                const variableRegex = /\{\{(\w+)\}\}/g;
                text = text.replace(variableRegex, (match, key) => {
                    const value = values[key];
                    if (value !== undefined && value !== '') {
                        // Just insert the value as bold text for filled values
                        return `<strong style="font-weight:600;">${value}</strong>`;
                    }
                    // Show placeholder as underlined blank for unfilled
                    return `<span style="text-decoration:underline;">_______________</span>`;
                });

                return { ...block, renderedText: text };
            });
    }, [template, values]);

    if (!template) {
        return <div className="preview-pane">No template to preview.</div>;
    }

    // Professional legal document styles
    const containerStyle = {
        fontFamily: '"Times New Roman", Times, Georgia, serif',
        fontSize: '12pt',
        lineHeight: '1.8',
        color: '#000',
        background: '#fff',
        padding: '1in 0.75in',
        maxWidth: '8.5in',
        minHeight: '11in',
        margin: '0 auto',
        boxSizing: 'border-box',
    };

    const headingStyle = {
        fontFamily: '"Times New Roman", Times, Georgia, serif',
        fontSize: '14pt',
        fontWeight: 'bold',
        textAlign: 'center',
        textTransform: 'uppercase',
        letterSpacing: '3px',
        marginTop: '28px',
        marginBottom: '20px',
        color: '#000',
        borderBottom: '1px solid #000',
        paddingBottom: '8px',
    };

    const paragraphStyle = {
        marginBottom: '18px',
        textAlign: 'justify',
        textIndent: '0',
        lineHeight: '1.8',
    };

    const clauseStyle = {
        marginBottom: '18px',
        textAlign: 'justify',
        lineHeight: '1.8',
        marginLeft: '20px',
    };

    const signatureStyle = {
        marginTop: '60px',
        paddingTop: '30px',
        lineHeight: '2.2',
        fontFamily: '"Times New Roman", Times, Georgia, serif',
    };

    return (
        <div className="preview-pane" style={containerStyle}>
            {/* Document Header */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h1 style={{
                    fontFamily: '"Times New Roman", Times, Georgia, serif',
                    fontSize: '18pt',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '4px',
                    marginBottom: '10px',
                    borderBottom: '2px solid #000',
                    paddingBottom: '10px',
                }}>
                    {template.title || 'Legal Document'}
                </h1>
            </div>

            {renderedContent.map((block) => {
                switch (block.type) {
                    case 'heading':
                        return (
                            <h2
                                key={block.id}
                                style={headingStyle}
                                dangerouslySetInnerHTML={{ __html: block.renderedText }}
                            />
                        );
                    case 'paragraph':
                        return (
                            <p
                                key={block.id}
                                style={paragraphStyle}
                                dangerouslySetInnerHTML={{ __html: block.renderedText.replace(/\n\n/g, '</p><p style="margin-bottom:18px;text-align:justify;line-height:1.8;">').replace(/\n/g, '<br/>') }}
                            />
                        );
                    case 'clause':
                        return (
                            <div
                                key={block.id}
                                style={clauseStyle}
                                dangerouslySetInnerHTML={{ __html: block.renderedText.replace(/\n\n/g, '</p><p style="margin-bottom:12px;text-align:justify;">').replace(/\n/g, '<br/>') }}
                            />
                        );
                    case 'table':
                        return (
                            <div
                                key={block.id}
                                style={{ margin: '20px 0', overflowX: 'auto' }}
                                dangerouslySetInnerHTML={{ __html: block.renderedText }}
                            />
                        );
                    case 'signature':
                        return (
                            <div
                                key={block.id}
                                style={signatureStyle}
                                dangerouslySetInnerHTML={{ __html: block.renderedText.replace(/\n/g, '<br/>') }}
                            />
                        );
                    case 'pagebreak':
                        return (
                            <div
                                key={block.id}
                                style={{
                                    pageBreakAfter: 'always',
                                    borderTop: '1px dashed #ccc',
                                    margin: '40px 0',
                                    height: '1px',
                                }}
                            />
                        );
                    default:
                        return null;
                }
            })}

            {/* Footer */}
            <div style={{
                marginTop: '40px',
                paddingTop: '20px',
                borderTop: '1px solid #ccc',
                textAlign: 'center',
                fontSize: '9pt',
                color: '#666',
            }}>
                Page 1 of 1
            </div>
        </div>
    );
};

export default PreviewPane;


